import {
  ArrowRightIcon,
  ChevronLeftIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/20/solid';
import { CSSProperties, RefObject, useEffect, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { ButtonV2, Textarea } from 'src/shared/components/ui';

interface CaptionModalProps<T extends { id: string; caption?: string }> {
  isOpen: boolean;
  onClose: () => void;
  items: T[];
  currentIndex: number;
  captionValue: string;
  onCaptionChange: (value: string) => void;
  isEditMode: boolean;
  onSave: () => Promise<void> | void;
  onNext: () => Promise<void> | void;
  onPrevious: () => Promise<void> | void;
  onDone: () => Promise<void> | void;
  renderPreview: (item: T, previewUrl: string | undefined) => React.ReactNode;
  getPreviewUrl: (item: T) => string | undefined;
  width?: string; // e.g., "w-1/2" or "w-[58%]"
  placeholder?: string;
  /** When true, the modal covers the entire screen without width constraints or extra padding */
  isFullScreen?: boolean;
  /** When true, shows carousel navigation but "Add" adds current item instead of advancing */
  isViewerMode?: boolean;
  /** Optional ref to focus after closing modal with ESC key */
  focusAfterClose?: RefObject<HTMLElement>;
  /** Optional content rendered below the caption input (e.g. a warning for the current item). */
  warning?: React.ReactNode;
}

/**
 * Reusable caption modal component for adding/editing captions
 */
export function CaptionModal<T extends { id: string; caption?: string }>({
  isOpen,
  onClose,
  items,
  currentIndex,
  captionValue,
  onCaptionChange,
  isEditMode,
  onSave,
  onNext,
  onPrevious,
  onDone,
  renderPreview,
  getPreviewUrl,
  width = 'w-1/2',
  placeholder = 'What do you like about this image?',
  isFullScreen = false,
  isViewerMode = false,
  focusAfterClose,
  warning
}: CaptionModalProps<T>) {
  const currentItem = items[currentIndex];
  const previewUrl = currentItem ? getPreviewUrl(currentItem) : undefined;
  const [overlayStyle, setOverlayStyle] = useState<CSSProperties | undefined>(
    undefined
  );

  // Sync overlay size with the visual viewport so the keyboard shrinks the
  // scrollable area instead of overlapping it (critical for iOS Safari where
  // window.innerHeight doesn't change when the keyboard opens).
  useEffect(() => {
    if (!isOpen) return;

    const vv = window.visualViewport;

    if (!vv) return;

    const syncWithViewport = () => {
      setOverlayStyle({
        height: `${vv.height}px`,
        top: `${vv.offsetTop}px`
      });
    };

    syncWithViewport();
    vv.addEventListener('resize', syncWithViewport);
    vv.addEventListener('scroll', syncWithViewport);

    return () => {
      vv.removeEventListener('resize', syncWithViewport);
      vv.removeEventListener('scroll', syncWithViewport);
    };
  }, [isOpen]);

  const handleBackdropClick = async (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      await onSave();
      onClose();
    }
  };

  const handleCloseClick = async () => {
    await onSave();
    onClose();
  };

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseClick();
        // Focus the provided element to prevent focus jumping to another input
        if (focusAfterClose?.current) {
          setTimeout(() => {
            focusAfterClose.current?.focus();
          }, 0);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, focusAfterClose]);

  if (!currentItem) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeIn', delay: 0.15 }}
          className={`fixed mobile:top-0 mobile:left-0 mobile:right-0 mobile:h-dvh z-[9999] flex items-center justify-center ${
            isFullScreen
              ? 'inset-0'
              : `top-0 right-0 bottom-0 ${width} mobile:w-full`
          }`}
          style={overlayStyle}
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.25,
              ease: [0.4, 0, 0.2, 1]
            }}
            className={`cursor-pointer bg-[#F9FAFB66]/40 backdrop-blur-md shadow-none w-full h-full mobile:rounded-none p-6 flex flex-col items-center justify-center mobile:justify-start mobile:pt-16 mobile:pb-8 gap-6 overflow-y-auto ${
              isFullScreen ? '' : 'mobile:pr-6 pr-[140px]'
            }`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {/* Preview Display with Carousel Effect */}
            <div
              className={`relative inline-flex items-center justify-center ${
                isFullScreen
                  ? 'mobile:h-[50vh] h-[calc(100vh-280px)] w-[90vw] max-w-[1200px]'
                  : 'mobile:h-[280px] h-[420px]'
              }`}
            >
              {/* Close button */}
              <button
                onClick={handleCloseClick}
                className={`absolute p-2 hover:bg-white/20 rounded-full transition-colors z-10 ${
                  isFullScreen
                    ? 'top-[0px] right-[0px] mobile:top-[-40px] mobile:right-[0]'
                    : 'top-[-64px] right-[-64px] mobile:top-[-64px] mobile:right-[-64px]'
                }`}
                aria-label="Close"
              >
                <XMarkIcon className="size-8 text-gray-700" />
              </button>
              <AnimatePresence mode="wait">
                {previewUrl && (
                  <motion.div
                    key={`${currentItem.id}-${currentIndex}`}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{
                      duration: 0.5,
                      delay: 0.25,
                      ease: [0.25, 0.1, 0.25, 1]
                    }}
                    className={`relative h-full flex items-center justify-center [&>img]:h-full [&>img]:w-auto [&>img]:max-w-[90vw] [&>img]:object-contain ${
                      isFullScreen ? 'w-full' : 'w-auto'
                    }`}
                  >
                    {renderPreview(currentItem, previewUrl)}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Caption Input */}
            <div className="w-full max-w-md flex flex-col gap-2">
              <Textarea
                classNames={{
                  input: '!bg-gray-1',
                  inputWrapper:
                    '!bg-gray-1 focus-within:!bg-gray-1 focus:!bg-gray-1 hover:!bg-gray-1 !border-gray-4 rounded-xl',
                  mainWrapper: '!bg-gray-1',
                  innerWrapper: 'items-end'
                }}
                value={captionValue}
                onChange={(e) => onCaptionChange(e.target.value)}
                placeholder={placeholder}
                rows={3}
                autoFocus
              />
              {warning}
            </div>

            {/* Navigation Buttons */}
            {isEditMode || items.length === 1 ? (
              // Edit mode or single item: Only show Done button
              <div className="flex justify-between w-full max-w-md">
                <ButtonV2 color="tertiary" onPress={onClose}>
                  Cancel
                </ButtonV2>

                <ButtonV2
                  color="primary"
                  onPress={async () => {
                    await onSave();
                    onDone();
                  }}
                  endContent={
                    !isEditMode ? <PlusIcon className="size-4" /> : undefined
                  }
                >
                  {isEditMode ? 'Save' : 'Add'}
                </ButtonV2>
              </div>
            ) : isViewerMode ? (
              // Viewer mode: Show carousel navigation with Add button that adds current item
              <div className="flex flex-col gap-4 w-full max-w-md">
                {/* Navigation row */}
                <div className="flex items-center justify-between w-full">
                  <ButtonV2
                    color="fourth"
                    onPress={onPrevious}
                    isDisabled={currentIndex === 0}
                    startContent={<ChevronLeftIcon className="size-4" />}
                  >
                    Previous
                  </ButtonV2>

                  {/* Progress indicator */}
                  {items.length > 1 && (
                    <div className="w-full flex flex-col gap-2">
                      <div className="flex gap-2 items-center justify-center">
                        {items.map((_, index) => (
                          <div
                            key={index}
                            className={`h-1.5 rounded-full transition-all ${
                              index === currentIndex
                                ? 'bg-primary w-8'
                                : 'bg-gray-4 w-1.5'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <ButtonV2
                    color="fourth"
                    onPress={onNext}
                    isDisabled={currentIndex === items.length - 1}
                    endContent={<ArrowRightIcon className="size-4" />}
                  >
                    Next
                  </ButtonV2>
                </div>

                {/* Add button row */}
                <div className="flex justify-center w-full">
                  <ButtonV2
                    color="primary"
                    onPress={async () => {
                      await onSave();
                      onDone();
                    }}
                    endContent={<PlusIcon className="size-4" />}
                  >
                    Add
                  </ButtonV2>
                </div>
              </div>
            ) : (
              // Multiple items and not in edit mode: Show Previous/Next navigation
              <div className="flex items-center justify-between w-full max-w-md">
                <ButtonV2
                  color="fourth"
                  onPress={onPrevious}
                  isDisabled={currentIndex === 0}
                  startContent={<ChevronLeftIcon className="size-4" />}
                >
                  Previous
                </ButtonV2>

                {/* Progress indicator - only show for multiple items */}
                {items.length > 1 && (
                  <div className="w-full flex flex-col gap-2">
                    <div className="flex gap-2 items-center justify-center">
                      {items.map((_, index) => (
                        <div
                          key={index}
                          className={`h-1.5 rounded-full transition-all ${
                            index === currentIndex
                              ? 'bg-primary w-8'
                              : 'bg-gray-4 w-1.5'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {currentIndex < items.length - 1 ? (
                  <ButtonV2
                    color="primary"
                    onPress={onNext}
                    endContent={<ArrowRightIcon className="size-4" />}
                  >
                    Add
                  </ButtonV2>
                ) : (
                  <ButtonV2
                    color="primary"
                    onPress={async () => {
                      await onNext();
                      onDone();
                    }}
                    endContent={<PlusIcon className="size-4" />}
                  >
                    Add
                  </ButtonV2>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
